import { useTranslation } from "react-i18next";
import { useLanguageController } from "../../i18n/useDashboardLanguage";
import { isLanguageContext } from "../../i18n/LanguageProvider";
import { RequestGeneration } from "../../i18n/requestGeneration";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { AdminPermission } from "../../lib/acl/permissions";
import { toast } from "../../lib/toast";

export type UserGroup = {
  id: number;
  name: string;
  description: string | null;
  appAccess: string[];
};

export type AuthUser = {
  id: number;
  username: string;
  userLevel: number;
  email?: string | null;
  lastVisit?: number | null;
  role?: string | null; // Added role field to AuthUser
  avatarUrl?: string | null;
  bannerUrl?: string | null;
  isBanned?: boolean;
  permissions?: AdminPermission[]; // Admin permissions from assigned roles
  groups?: UserGroup[]; // User's group memberships
};

export type AuthSecurity = {
  twoFactorEnabled: boolean;
  passkeyCount: number;
};

export type AuthImpersonation = {
  active: boolean;
  adminUserId: number | null;
};

export type AppConfig = {
  instanceName?: string;
  defaultPage?: string;
  notifications: {
    externalChannelsDisabledForNonAdmin: boolean;
    emailConfigured: boolean;
    signalEnabled: boolean;
    discordBotEnabled: boolean;
    discordInviteLink: string | null;
    /**
     * Install-wide command-channel master enable, emitted by `meRoute`. Gates the
     * profile command sub-tab + sidebar entry; OFF by default. Optional for forward
     * compatibility with older payloads. The panel still self-gates via
     * `GET /api/me/command-channel/enabled` as defense-in-depth.
     */
    commandChannelEnabled?: boolean;
  };
  features: {
    registrationEnabled: boolean;
    webconsoleEnabled: boolean;
    wsdashboardEnabled: boolean;
    wsdMirrorEnabled?: boolean;
    configEditingEnabled: boolean;
    /**
     * Install-wide master enable for inter-instance data transfer. Gates the
     * Transfers nav section + `/transfers` surface; OFF by default. Optional for
     * forward compatibility until `meRoute` emits it.
     */
    interInstanceTransferEnabled?: boolean;
    /**
     * Install-wide master enable for the large-file-delete Delete Protection
     * surface. Gates the admin route + QuickNav link; OFF by default. Optional for
     * forward compatibility with older payloads.
     */
    deleteProtectionEnabled?: boolean;
  };
  uploads: {
    avatarMaxSizeKb: number;
    bannerMaxSizeKb: number;
    avatarMaxBytes: number;
    bannerMaxBytes: number;
    allowAvatarUpload: boolean;
  };
  branding: {
    defaultAvatarUrl: string;
    defaultBannerUrl: string | null;
  };
  maintenance: {
    enabled: boolean;
    message: string | null;
    enabledAt: string | null;
    enabledByUserId: number | null;
    epoch: number;
  };
};

type AuthContextValue = {
  user: AuthUser | null;
  security: AuthSecurity | null;
  appConfig: AppConfig | null;
  impersonation: AuthImpersonation | null;
  csrfToken: string | null;
  loading: boolean;
  statusCode: number | null;
  refresh: (options?: { background?: boolean }) => Promise<void>;
  clear: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const HEARTBEAT_INTERVAL_MS = 30_000;

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation("auth");
  const translateRef = useRef(t);
  translateRef.current = t;
  const languageController = useLanguageController();
  const requests = useRef(new RequestGeneration());
  const [user, setUser] = useState<AuthUser | null>(null);
  const [security, setSecurity] = useState<AuthSecurity | null>(null);
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null);
  const [impersonation, setImpersonation] = useState<AuthImpersonation | null>(null);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusCode, setStatusCode] = useState<number | null>(null);
  const prevBannedRef = useRef<boolean | undefined>(undefined);

  const refresh = useCallback(async (options?: { background?: boolean; signal?: AbortSignal }) => {
    const ticket = requests.current.next();
    const isCurrent = () => requests.current.current(ticket) && !options?.signal?.aborted;
    const isBackground = options?.background === true;

    if (!isBackground) {
      setLoading(true);
    }
    try {
      const res = await fetch("/api/me", { signal: options?.signal });
      if (!isCurrent()) return;
      setStatusCode(res.status);
      if (!res.ok) {
        // Background polls: only clear auth state on definitive 401 (session
        // expired). Other HTTP errors (502/503 during restart, 500, etc.)
        // are transient — preserve existing auth state so the user isn't
        // kicked to /login while the backend restarts (e.g., during update).
        if (res.status === 401) {
          languageController.setIdentity(null, false, null);
          setUser(null);
          setSecurity(null);
          setAppConfig(null);
          setImpersonation(null);
          setCsrfToken(null);
        } else { languageController.contextFailed(); }
      } else {
        const data = await res.json();
        if (!isCurrent()) return;
        const newUser: AuthUser | null = data.user ?? null;
        languageController.setIdentity(newUser ? `${newUser.id}:${data.impersonation?.active ? data.impersonation.adminUserId : "self"}:${isLanguageContext(data.language) ? data.language.sessionContext : "unavailable"}` : null, data.impersonation?.active === true, isLanguageContext(data.language) ? data.language : null);
        setUser(newUser);
        setSecurity(data.security ?? null);
        setAppConfig(data.appConfig ?? null);
        setImpersonation(data.impersonation ?? null);
        setCsrfToken(data.csrfToken ?? null);

        // Detect ban transition: was not banned → now banned
        if (newUser?.isBanned && prevBannedRef.current === false) {
          toast.error(translateRef.current($ => $.suspended), {
            duration: 15_000,
          });
        }
        prevBannedRef.current = newUser?.isBanned;
      }
    } catch (err) {
      if (!isCurrent()) return;
      // A transient transport/JSON failure is not evidence of logout. Initial
      // bootstrap already has null identity; known users remain signed in.
      languageController.contextFailed();
      setStatusCode(null);
    } finally {
      if (isCurrent()) {
        setLoading(false);
      }
    }
  }, [languageController]);

  useEffect(() => { languageController.setRefresh(() => refresh({ background: true })); }, [languageController, refresh]);

  const clear = useCallback(() => {
    requests.current.next();
    languageController.invalidateIdentity();
    setLoading(false);
    prevBannedRef.current = undefined;
    setUser(null);
    setSecurity(null);
    setAppConfig(null);
    setImpersonation(null);
    setCsrfToken(null);
  }, [languageController]);

  // Initial fetch
  useEffect(() => {
    const controller = new AbortController();
    refresh({ signal: controller.signal });
    return () => { controller.abort(); requests.current.next(); };
  }, []);

  // Heartbeat: poll /api/me every 30s when tab is visible and user is logged in
  useEffect(() => {
    // Only start heartbeat after initial load
    if (loading) return;
    // Only heartbeat when we have a user
    if (!user) return;

    let timer: ReturnType<typeof setInterval> | null = null;

    const tick = () => {
      if (document.hidden) return;
      void refresh({ background: true });
    };

    const handleVisibility = () => {
      if (!document.hidden) {
        // Resume immediately on tab focus
        void refresh({ background: true });
      }
    };

    timer = setInterval(tick, HEARTBEAT_INTERVAL_MS);
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleVisibility);

    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleVisibility);
    };
  }, [loading, user !== null, refresh]);

  const value = useMemo(
    () => ({ user, security, appConfig, impersonation, csrfToken, loading, statusCode, refresh, clear }),
    [user, security, appConfig, impersonation, csrfToken, loading, statusCode, refresh, clear]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
};
